import { useState, useEffect, useCallback } from "react";
import PouchDB from "pouchdb-browser";
import { pouches_db, localPouchPrefix } from "../config";
import { logger } from "../functions/logger";

const cache = {}; // Simple in-memory cache
const cacheTimestamps = {}; // Store cache timestamps to manage staleness

/**
 * Wipe the whole in-memory list cache. MUST be called wherever the local DBs
 * are destroyed (cuadre switch / logout), otherwise a route change can serve the
 * previous route's docs from this module-scoped cache (it survives the SPA
 * session since login/logout use history.replace, not a full reload).
 */
export function clearDataListCache() {
  for (const k in cache) delete cache[k];
  for (const k in cacheTimestamps) delete cacheTimestamps[k];
}

// Diagnostic: gastos are stamped with the originating sync prefix (usePouch.putDoc)
// + creditor_id/collect_date. If a gastos read contains docs whose prefix != the
// current one, we are serving/holding a previous route's data — log it so the next
// occurrence is diagnosable. Gated on foreign docs so normal reads never log.
function reportForeignGastos(action, message, docs, currentPrefix, extra = {}) {
  const foreign = docs.filter((d) => d && d.prefix && currentPrefix && d.prefix !== currentPrefix);
  if (!foreign.length) return;
  logger.warning(action, message, {
    entity: "gastos",
    context: {
      current_prefix: currentPrefix,
      total: docs.length,
      foreign_count: foreign.length,
      foreign_prefixes: [...new Set(foreign.map((d) => d.prefix))],
      foreign_ids: foreign.slice(0, 20).map((d) => d._id),
      ...extra,
    },
  });
}

/**
 * Custom hook to fetch data from PouchDB with stale-while-revalidate.
 * @param {string} dbName - The PouchDB database name.
 * @param {object} [newConfigs={}] - { options, cacheTime }.
 * @returns {{ data: any, isLoading: boolean, refresh: Function, error: Error|null }}
 */
export default function useDataList(dbName, newConfigs = {}) {
  const configs = { options: {}, cacheTime: 300000, ...newConfigs };

  const { options, cacheTime } = configs;

  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scope the cache key by the active sync prefix (the cuadre/route) AND the
  // include_deleted option. This is the core fix: a new route has a new prefix,
  // so it can never read a previous route's cached slot, and the Logout page's
  // include_deleted read no longer collides with Spenses' live read.
  const prefix = (typeof localStorage !== "undefined" && localStorage.getItem(localPouchPrefix)) || "none";
  const scope = options?.include_deleted ? "withDeleted" : "live";
  const cacheKey = `${dbName}:${prefix}:${scope}`;

  // Define fetchData outside of useEffect so it can be reused
  const fetchData = useCallback(async () => {
    const db = new PouchDB(dbName);

    // Return cached data immediately if available and valid
    if (cache[cacheKey] && Date.now() - cacheTimestamps[cacheKey] < cacheTime) {
      setData(cache[cacheKey]);
      setLoading(false);

      if (dbName === pouches_db.GASTOS) {
        reportForeignGastos(
          "gastos.cache_served_foreign",
          "Served previous-route gastos from in-memory cache",
          cache[cacheKey],
          prefix,
          { cache_key: cacheKey, cache_age_ms: Date.now() - cacheTimestamps[cacheKey] }
        );
      }
    }

    // Fetch fresh data to update the cache
    try {
      let freshData;

      // Fetch all documents
      const result = await db.allDocs({ include_docs: true, ...options });
      freshData = options?.include_deleted
        ? result.rows.map((row) => row.doc)
        : result.rows.map((row) => row.doc).filter((x) => !x.deleted);

      if (dbName === pouches_db.GASTOS) {
        reportForeignGastos(
          "gastos.local_db_foreign",
          "Local gastos DB physically contains previous-route docs",
          freshData,
          prefix,
          { cache_key: cacheKey }
        );
      }

      cache[cacheKey] = freshData; // Update cache
      cacheTimestamps[cacheKey] = Date.now(); // Update timestamp

      setData(freshData);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err);
      setData([]);
      setLoading(false);
    }
  }, [cacheKey, cacheTime, dbName, options, prefix]);

  useEffect(() => {
    let isMounted = true;

    // Wrap fetchData to ensure it respects component mounting
    const fetchDataIfMounted = async () => {
      if (isMounted) {
        await fetchData();
      }
    };

    fetchDataIfMounted();

    return () => {
      isMounted = false; // Prevent state updates after unmounting
    };
  }, [dbName, cacheTime, cacheKey]);

  return { data, isLoading, refresh: fetchData, error };
}
